import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { glass } from "./portal";
import { useProject } from "@/lib/portal/useProject";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Star, Send } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";

export const Route = createFileRoute("/_authenticated/portal/tickets")({
  component: TicketsPage,
});

const CATEGORIES = [
  { v: "bug", l: "Bug Report" },
  { v: "website_update", l: "Website Update" },
  { v: "new_feature", l: "New Feature" },
  { v: "seo_request", l: "SEO Request" },
  { v: "tech_support", l: "Technical Support" },
  { v: "emergency", l: "Emergency" },
];
const PRIORITY = ["low", "medium", "high", "urgent"];

const STATUS_COLORS: Record<string, string> = {
  open: "bg-blue-500/20 text-blue-300",
  waiting: "bg-amber-500/20 text-amber-300",
  in_progress: "bg-fuchsia-500/20 text-fuchsia-300",
  testing: "bg-cyan-500/20 text-cyan-300",
  completed: "bg-emerald-500/20 text-emerald-300",
};

type Ticket = { id: string; code: string; title: string; category: string; priority: string; status: string; assigned_developer: string | null; progress: number; created_at: string; rating: number | null };

function TicketsPage() {
  const qc = useQueryClient();
  const { data: project } = useProject();
  const [newOpen, setNewOpen] = useState(false);
  const [active, setActive] = useState<Ticket | null>(null);
  const [form, setForm] = useState({ title: "", category: "website_update", priority: "medium", description: "" });

  const { data: tickets } = useQuery({
    queryKey: ["portal", "tickets"],
    queryFn: async () => ((await supabase.from("tickets").select("*").order("created_at", { ascending: false })).data ?? []) as Ticket[],
    enabled: !!project,
  });

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!project) return;
    const { data: user } = await supabase.auth.getUser();
    const { data: inserted, error } = await supabase.from("tickets").insert({
      project_id: project.id, created_by: user.user!.id, code: "",
      title: form.title, category: form.category, priority: form.priority,
      description: form.description, status: "open", progress: 0,
    }).select("code, title, priority, category").single();
    if (error) return toast.error(error.message);
    setNewOpen(false);
    setForm({ title: "", category: "website_update", priority: "medium", description: "" });
    toast.success("Ticket creato");
    qc.invalidateQueries({ queryKey: ["portal", "tickets"] });
    // Fire-and-forget WhatsApp notification to admin
    if (inserted) {
      import("@/lib/portal/notify.functions").then(({ notifyNewTicket }) => {
        notifyNewTicket({ data: {
          code: inserted.code, title: inserted.title,
          priority: inserted.priority, category: inserted.category,
          author: user.user?.email ?? undefined,
        } }).catch(() => {});
      });
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Tickets</h1>
          <p className="text-white/60 mt-1">Sistema professionale di gestione richieste.</p>
        </div>
        <button onClick={() => setNewOpen(true)} className="px-4 py-2 rounded-full bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black text-sm font-semibold flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Nuovo ticket
        </button>
      </div>

      <div className={`${glass} overflow-hidden`}>
        <div className="grid grid-cols-[80px_1fr_auto_auto] sm:grid-cols-[100px_1fr_120px_140px_100px_120px] px-4 py-3 text-[10px] uppercase tracking-widest text-white/40 border-b border-white/5">
          <div>ID</div><div>Titolo</div><div className="hidden sm:block">Categoria</div><div className="hidden sm:block">Developer</div><div>Priorità</div><div>Status</div>
        </div>
        {(tickets ?? []).map((t) => (
          <button key={t.id} onClick={() => setActive(t)}
            className="w-full grid grid-cols-[80px_1fr_auto_auto] sm:grid-cols-[100px_1fr_120px_140px_100px_120px] items-center px-4 py-3 text-sm border-b border-white/5 last:border-b-0 hover:bg-white/5 text-left">
            <div className="font-mono text-white/60">{t.code}</div>
            <div className="font-medium truncate pr-2">{t.title}</div>
            <div className="hidden sm:block text-white/60 text-xs">{t.category.replace("_", " ")}</div>
            <div className="hidden sm:block text-white/60 text-xs">{t.assigned_developer ?? "—"}</div>
            <div className={`text-xs ${t.priority === "urgent" ? "text-red-400" : t.priority === "high" ? "text-orange-400" : "text-white/60"}`}>{t.priority}</div>
            <div><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${STATUS_COLORS[t.status] ?? "bg-white/5"}`}>{t.status.replace("_", " ")}</span></div>
          </button>
        ))}
        {(tickets ?? []).length === 0 && <div className="p-8 text-center text-white/50 text-sm">Nessun ticket ancora. Aprine uno!</div>}
      </div>

      <AnimatePresence>
        {newOpen && (
          <Modal onClose={() => setNewOpen(false)} title="Nuovo ticket">
            <form onSubmit={create} className="space-y-3">
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo" className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
                  {CATEGORIES.map((c) => <option key={c.v} value={c.v} className="bg-black">{c.l}</option>)}
                </select>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm">
                  {PRIORITY.map((p) => <option key={p} value={p} className="bg-black capitalize">{p}</option>)}
                </select>
              </div>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} placeholder="Descrivi la richiesta..." className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
              <button type="submit" className="w-full py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-black text-sm font-semibold">Crea ticket</button>
            </form>
          </Modal>
        )}
        {active && <TicketDetail ticket={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </div>
  );
}

function TicketDetail({ ticket, onClose }: { ticket: Ticket; onClose: () => void }) {
  const qc = useQueryClient();
  const [msg, setMsg] = useState("");
  const [rating, setRating] = useState(0);
  const { data: msgs } = useQuery({
    queryKey: ["portal", "ticket-msgs", ticket.id],
    queryFn: async () => (await supabase.from("ticket_messages").select("*").eq("ticket_id", ticket.id).order("created_at")).data ?? [],
  });
  const send = async () => {
    if (!msg.trim()) return;
    const { data: user } = await supabase.auth.getUser();
    await supabase.from("ticket_messages").insert({ ticket_id: ticket.id, sender_id: user.user!.id, sender_role: "client", body: msg });
    setMsg("");
    qc.invalidateQueries({ queryKey: ["portal", "ticket-msgs", ticket.id] });
  };
  const rate = async (n: number) => {
    setRating(n);
    await supabase.from("tickets").update({ rating: n }).eq("id", ticket.id);
    toast.success("Grazie per la valutazione");
    qc.invalidateQueries({ queryKey: ["portal", "tickets"] });
  };
  return (
    <Modal onClose={onClose} title={`${ticket.code} · ${ticket.title}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-xs">
        <Info label="Categoria" value={ticket.category.replace("_", " ")} />
        <Info label="Priorità" value={ticket.priority} />
        <Info label="Status" value={ticket.status.replace("_", " ")} />
        <Info label="Developer" value={ticket.assigned_developer ?? "—"} />
      </div>
      <div className="h-1.5 rounded-full bg-white/5 mb-4 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${ticket.progress}%` }} />
      </div>
      <div className="max-h-60 overflow-y-auto space-y-2 mb-3">
        {(msgs ?? []).map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "client" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.sender_role === "client" ? "bg-gradient-to-br from-fuchsia-500 to-cyan-400 text-black" : "bg-white/5 border border-white/10"}`}>
              {m.body}
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mb-3">
        <input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Rispondi..." className="flex-1 rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm" />
        <button className="p-2 rounded-xl bg-white text-black"><Send className="h-4 w-4" /></button>
      </form>
      {ticket.status === "completed" && !ticket.rating && (
        <div className="rounded-xl bg-white/5 border border-white/10 p-3 flex items-center justify-between">
          <span className="text-sm">Come è stato risolto?</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => rate(n)}><Star className={`h-5 w-5 ${n <= rating ? "text-amber-300 fill-amber-300" : "text-white/30"}`} /></button>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div className="rounded-lg bg-white/5 border border-white/10 p-2"><div className="text-white/40">{label}</div><div className="font-medium capitalize">{value}</div></div>;
}

function Modal({ children, onClose, title }: { children: React.ReactNode; onClose: () => void; title: string }) {
  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
      <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg p-6 rounded-3xl bg-[#0f0f16] border border-white/10 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-white/10"><X className="h-4 w-4" /></button>
        </div>
        {children}
      </motion.div>
    </>
  );
}

// suppress unused imports
void it; void format;
